/**
 * The run page's Review stage (missions vision, section 3): what the agent delivered against its
 * criteria, what it changed and what the checks say, then the three decisions, all in place:
 * Accept and land (or Accept and close as done when there is no branch to land), Send back with a
 * note (another session like the last start, carrying the note), Split follow-up (a new item with
 * the criteria not met). Nothing opens a dialog or leaves the page.
 */
import { CircleCheck, CircleX, CornerUpLeft, GitMerge, Split } from '@orgus/ui/icons';
import { useState } from 'react';
import { Link } from 'react-router';
import { criterionText } from '@orgus/protocol';
import type { BacklogItem, Landing, Mission, MissionLaunch } from '@orgus/protocol';
import { Button, ErrorState, Input, Panel, Textarea, cn, toast } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { toUiError } from '../../../lib/api.ts';
import { submitLanding } from '../../landing/api.ts';
import { createItem, startMission } from '../data.ts';

/** Where the header's Review button takes focus. */
export const REVIEW_ANCHOR = 'run-review';

export function focusReview(): void {
  const el = document.getElementById(REVIEW_ANCHOR);
  el?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  el?.querySelector<HTMLElement>('button')?.focus({ preventScroll: true });
}

type Mode = 'idle' | 'back' | 'split';

export interface ReviewVerdicts {
  met: number;
  total: number;
  /** Criteria the report says are not met, with what it said. */
  unmet: { id: string; text: string; evidence: string }[];
}

/** The criteria as the review reads them: checked off or reported met, and the ones that are not. */
export function reviewVerdicts(item: BacklogItem | null, mission: Pick<Mission, 'report'>): ReviewVerdicts | null {
  if (!item?.acceptance.length) return null;
  const said = new Map((mission.report?.acceptance ?? []).map((a) => [a.id, a]));
  const unmet = item.acceptance.filter((c) => !c.done && said.get(c.id)?.met !== true).map((c) => ({ id: c.id, text: criterionText(c), evidence: said.get(c.id)?.evidence ?? '' }));
  return { met: item.acceptance.length - unmet.length, total: item.acceptance.length, unmet };
}

export function RunReview({
  mission,
  item,
  verdicts,
  branch,
  target,
  canLand,
  launch,
  sessionId,
  checks,
  onAccept,
  accepting,
}: {
  mission: Mission;
  item: BacklogItem | null;
  verdicts: ReviewVerdicts | null;
  branch: string | null;
  /** The branch it lands on: the project's default. */
  target: string;
  /** The landing queue runs here and the branch is not the target itself. */
  canLand: boolean;
  launch: MissionLaunch | null;
  sessionId: string | null;
  /** The last landing for the branch, when there was one (a failed check sends it back here). */
  checks: Landing | null;
  /** Accept without landing: close the mission as done and its item with it. */
  onAccept: () => Promise<void>;
  accepting: boolean;
}) {
  const [mode, setMode] = useState<Mode>('idle');
  const [note, setNote] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState<'land' | 'back' | 'split' | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  const [created, setCreated] = useState<BacklogItem | null>(null);

  const land = async () => {
    if (!branch) return;
    setBusy('land');
    setError(null);
    try {
      await submitLanding({ projectId: mission.projectId, branch, title: mission.title, missionId: mission.id, ...(launch?.checkoutId ? { checkoutId: launch.checkoutId } : {}), ...(sessionId ? { sessionId } : {}) }, target);
      toast.success('Submitted to the landing queue', `${branch} lands on ${target} once its checks pass.`);
    } catch (e) {
      setError(toUiError(e, { what: `${branch} was not submitted to the landing queue.`, next: 'Fix what it says, then accept again.' }));
    } finally {
      setBusy(null);
    }
  };

  const sendBack = async () => {
    if (!launch || !note.trim()) return;
    setBusy('back');
    setError(null);
    try {
      // The same machine and folder as the last start, so the session picks up the branch where it is.
      const where = launch.checkoutId && !launch.worktree ? { checkoutId: launch.checkoutId } : { cwd: launch.worktree ?? launch.cwd ?? '' };
      await startMission(mission.id, { machineId: launch.machineId, ...where, mode: 'headless', model: launch.model, note: note.trim() });
      toast.success('Sent back', 'A new session starts with your note.');
      setNote('');
      setMode('idle');
    } catch (e) {
      setError(toUiError(e, { what: 'The mission was not sent back.', next: 'Check that the machine is online, then send it again.' }));
    } finally {
      setBusy(null);
    }
  };

  const split = async () => {
    if (!item || !title.trim()) return;
    setBusy('split');
    setError(null);
    try {
      const it = await createItem({
        projectId: item.projectId,
        kind: item.kind === 'epic' || item.kind === 'feature' ? 'story' : item.kind,
        title: title.trim(),
        parentId: item.parentId,
        priority: item.priority,
        acceptance: (verdicts?.unmet ?? []).map((u) => ({ format: 'check' as const, text: u.text })),
        body: `Follow-up of ${item.key}: what its mission left open.`,
      });
      setCreated(it);
      setMode('idle');
    } catch (e) {
      setError(toUiError(e, { what: 'The follow-up was not created.', next: 'Check the title, then try again.' }));
    } finally {
      setBusy(null);
    }
  };

  const openSplit = () => {
    setTitle(verdicts?.unmet[0] ? `Follow up: ${verdicts.unmet[0].text}`.slice(0, 200) : `Follow up: ${item?.title ?? mission.title}`.slice(0, 200));
    setMode(mode === 'split' ? 'idle' : 'split');
  };

  const allMet = !verdicts || verdicts.unmet.length === 0;
  const checkFailed = checks?.status === 'failed' || checks?.status === 'parked';

  return (
    <Panel id={REVIEW_ANCHOR} title="Review" sub={verdicts ? `${verdicts.met} of ${verdicts.total} criteria met` : undefined} aria-label="Review" bodyClassName="flex flex-col gap-4">
      {verdicts && verdicts.unmet.length > 0 && (
        <ul className="flex flex-col gap-2" aria-label="Criteria not met">
          {verdicts.unmet.map((u) => (
            <li key={u.id} className="flex min-w-0 items-start gap-2">
              <span className="flex h-5 shrink-0 items-center text-status-danger">
                <CircleX className="size-4" aria-hidden />
              </span>
              <span className="flex min-w-0 flex-col gap-1">
                <span className="text-ui text-fg">{u.text}</span>
                {u.evidence && <span className="line-clamp-2 text-caption text-fg-subtle">{u.evidence}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
      {verdicts && allMet && (
        <p className="flex items-center gap-2 text-ui text-fg-muted">
          <CircleCheck className="size-4 shrink-0 text-status-success" aria-hidden />
          Every criterion is met
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {canLand && branch ? (
          <Button variant="primary" icon={<GitMerge />} loading={busy === 'land'} disabled={!!busy} onClick={() => void land()}>
            Accept and land
          </Button>
        ) : (
          // After a failed check, sending it back to fix leads; accepting as it is stays in reach.
          <Button variant={checkFailed ? 'ghost' : 'primary'} icon={<CircleCheck />} loading={accepting} disabled={!!busy} onClick={() => void onAccept()} className={checkFailed ? 'order-last' : undefined}>
            Accept and close as done
          </Button>
        )}
        <Button variant={checkFailed ? 'primary' : 'secondary'} icon={<CornerUpLeft />} aria-expanded={mode === 'back'} disabled={!launch || !!busy} onClick={() => setMode(mode === 'back' ? 'idle' : 'back')}>
          Send back
        </Button>
        {item && (
          <Button variant="ghost" icon={<Split />} aria-expanded={mode === 'split'} disabled={!!busy} onClick={openSplit}>
            Split follow-up
          </Button>
        )}
        {canLand && branch && (
          <span className="min-w-0 truncate text-caption text-fg-subtle">
            <span className="font-mono">{branch}</span> to <span className="font-mono">{target}</span>
          </span>
        )}
      </div>

      {mode === 'back' && (
        <div className={cn('flex flex-col gap-2')}>
          <Textarea
            label="What should change"
            rows={3}
            autoFocus
            value={note}
            maxLength={4000}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void sendBack();
              if (e.key === 'Escape') setMode('idle');
            }}
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setMode('idle')}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" icon={<CornerUpLeft />} loading={busy === 'back'} disabled={!note.trim()} shortcut={['Ctrl', 'Enter']} onClick={() => void sendBack()}>
              Send back
            </Button>
          </div>
        </div>
      )}

      {mode === 'split' && (
        <div className="flex flex-col gap-2">
          <Input
            label="Follow-up title"
            autoFocus
            value={title}
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void split();
              if (e.key === 'Escape') setMode('idle');
            }}
            hint={verdicts?.unmet.length ? `Takes the ${verdicts.unmet.length === 1 ? 'criterion' : `${verdicts.unmet.length} criteria`} not met` : undefined}
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setMode('idle')}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" icon={<Split />} loading={busy === 'split'} disabled={!title.trim()} onClick={() => void split()}>
              Create follow-up
            </Button>
          </div>
        </div>
      )}

      {created && (
        <p className="text-caption text-fg-subtle">
          Created{' '}
          <Link to={`/missions?item=${encodeURIComponent(created.id)}`} className="rounded-control font-mono text-fg-muted hover:text-fg">
            {created.key}
          </Link>{' '}
          {created.title}
        </p>
      )}
      {error && <ErrorState variant="inline" error={error} action={<Button variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</Button>} />}
    </Panel>
  );
}
